import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * Retorna a data atual considerando o fuso horário de Brasília (UTC-3)
 * Isso é importante para garantir que o "Hoje" seja consistente com a localização do negócio,
 * independentemente do fuso horário do dispositivo do usuário.
 */
export function getTodayInBrasilia(): Date {
  // Criar data com horário atual em UTC
  const now = new Date();
  
  // Obter offset de Brasília (UTC-3) em milissegundos
  // NOTA: Isso é uma aproximação fixa. Para suporte completo a horário de verão (se voltar)
  // ou mudanças de fuso, seria ideal usar bibliotecas como date-fns-tz
  const brasiliaOffset = -3 * 60 * 60 * 1000;
  
  // Ajustar para o horário de Brasília
  const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
  const brasiliaTime = new Date(utc + brasiliaOffset);
  
  // Retornar data zerada (00:00:00)
  brasiliaTime.setHours(0, 0, 0, 0);
  return brasiliaTime;
}

/**
 * Cria um objeto Date a partir de uma string YYYY-MM-DD, interpretando-a como data local (00:00:00)
 * Isso evita a conversão para UTC que ocorreria com new Date("YYYY-MM-DD")
 * @param dateStr - String no formato YYYY-MM-DD
 */
export function getBrasiliaDate(dateStr: string): Date {
  if (!dateStr) return getTodayInBrasilia();
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Converte data ISO para formato relativo humanizado (pt-BR)
 * @param isoDate - Data em formato ISO 8601 (ex: "2025-10-08 14:30")
 * @returns String formatada (ex: "15 min atrás", "2 horas atrás", "ontem")
 */
export function formatRelativeTime(isoDate: string): string {
  const now = new Date();
  const date = new Date(isoDate);
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  // Agora (menos de 10 segundos)
  if (diffSec < 10) {
    return 'agora';
  }

  // Segundos (10-59s)
  if (diffSec < 60) {
    return `${diffSec} seg atrás`;
  }

  // Minutos (1-59 min)
  if (diffMin < 60) {
    return `${diffMin} min atrás`;
  }

  // Horas (1-23 horas)
  if (diffHour < 24) {
    return diffHour === 1 ? '1 hora atrás' : `${diffHour} horas atrás`;
  }

  // Ontem (24-48 horas)
  if (diffDay === 1) {
    return 'ontem';
  }

  // Dias (2-6 dias)
  if (diffDay < 7) {
    return `${diffDay} dias atrás`;
  }

  // Semanas (7-29 dias)
  if (diffDay < 30) {
    const weeks = Math.floor(diffDay / 7);
    return weeks === 1 ? '1 semana atrás' : `${weeks} semanas atrás`;
  }

  // Meses (30-364 dias)
  if (diffDay < 365) {
    const months = Math.floor(diffDay / 30);
    return months === 1 ? '1 mês atrás' : `${months} meses atrás`;
  }

  // Anos (365+ dias)
  const years = Math.floor(diffDay / 365);
  return years === 1 ? '1 ano atrás' : `${years} anos atrás`;
}

/**
 * Remove caracteres não numéricos de um documento
 * @param documento - String contendo o documento com ou sem formatação
 * @returns String contendo apenas dígitos
 */
export function limparDocumento(documento: string): string {
  return documento.replace(/\D/g, '');
}

/**
 * Valida um CNPJ brasileiro usando o algoritmo de dígitos verificadores
 * @param cnpj - String contendo o CNPJ com ou sem formatação
 * @returns true se o CNPJ é válido, false caso contrário
 */
export function validarCNPJ(cnpj: string): boolean {
  const cnpjLimpo = limparDocumento(cnpj);

  // CNPJ deve ter 14 dígitos
  if (cnpjLimpo.length !== 14) {
    return false;
  }

  // CNPJ não pode ter todos os dígitos iguais
  if (cnpjLimpo.split('').every(char => char === cnpjLimpo[0])) {
    return false;
  }

  // Calcula primeiro dígito verificador
  let soma = 0;
  let peso = 5;
  for (let i = 0; i < 12; i++) {
    soma += parseInt(cnpjLimpo[i]) * peso;
    peso--;
    if (peso < 2) {
      peso = 9;
    }
  }

  const resto = soma % 11;
  const digito1 = resto < 2 ? 0 : 11 - resto;

  if (parseInt(cnpjLimpo[12]) !== digito1) {
    return false;
  }

  // Calcula segundo dígito verificador
  soma = 0;
  peso = 6;
  for (let i = 0; i < 13; i++) {
    soma += parseInt(cnpjLimpo[i]) * peso;
    peso--;
    if (peso < 2) {
      peso = 9;
    }
  }

  const resto2 = soma % 11;
  const digito2 = resto2 < 2 ? 0 : 11 - resto2;

  return parseInt(cnpjLimpo[13]) === digito2;
}

/**
 * Valida um CPF brasileiro usando o algoritmo de dígitos verificadores
 * @param cpf - String contendo o CPF com ou sem formatação
 * @returns true se o CPF é válido, false caso contrário
 */
export function validarCPF(cpf: string): boolean {
  const cpfLimpo = limparDocumento(cpf);

  // CPF deve ter 11 dígitos
  if (cpfLimpo.length !== 11) {
    return false;
  }

  // CPF não pode ter todos os dígitos iguais
  if (cpfLimpo.split('').every(char => char === cpfLimpo[0])) {
    return false;
  }

  // Calcula primeiro dígito verificador
  let soma = 0;
  for (let i = 0; i < 9; i++) {
    soma += parseInt(cpfLimpo[i]) * (10 - i);
  }

  const resto = soma % 11;
  const digito1 = resto < 2 ? 0 : 11 - resto;

  if (parseInt(cpfLimpo[9]) !== digito1) {
    return false;
  }

  // Calcula segundo dígito verificador
  soma = 0;
  for (let i = 0; i < 10; i++) {
    soma += parseInt(cpfLimpo[i]) * (11 - i);
  }

  const resto2 = soma % 11;
  const digito2 = resto2 < 2 ? 0 : 11 - resto2;

  return parseInt(cpfLimpo[10]) === digito2;
}

/**
 * Formata um CNPJ no padrão brasileiro: 00.000.000/0000-00
 * @param cnpj - String contendo o CNPJ com ou sem formatação
 * @returns CNPJ formatado ou string original se inválido
 */
export function formatarCNPJ(cnpj: string): string {
  const cnpjLimpo = limparDocumento(cnpj);

  if (cnpjLimpo.length !== 14) {
    return cnpj;
  }

  return `${cnpjLimpo.slice(0, 2)}.${cnpjLimpo.slice(2, 5)}.${cnpjLimpo.slice(5, 8)}/${cnpjLimpo.slice(8, 12)}-${cnpjLimpo.slice(12)}`;
}

/**
 * Formata um CPF no padrão brasileiro: 000.000.000-00
 * @param cpf - String contendo o CPF com ou sem formatação
 * @returns CPF formatado ou string original se inválido
 */
export function formatarCPF(cpf: string): string {
  const cpfLimpo = limparDocumento(cpf);

  if (cpfLimpo.length !== 11) {
    return cpf;
  }

  return `${cpfLimpo.slice(0, 3)}.${cpfLimpo.slice(3, 6)}.${cpfLimpo.slice(6, 9)}-${cpfLimpo.slice(9)}`;
}

/**
 * Aplica máscara de CNPJ durante digitação
 * @param value - Valor atual do input
 * @returns Valor formatado com máscara de CNPJ
 */
export function mascaraCNPJ(value: string): string {
  const cnpjLimpo = limparDocumento(value);

  if (cnpjLimpo.length <= 2) {
    return cnpjLimpo;
  }
  if (cnpjLimpo.length <= 5) {
    return `${cnpjLimpo.slice(0, 2)}.${cnpjLimpo.slice(2)}`;
  }
  if (cnpjLimpo.length <= 8) {
    return `${cnpjLimpo.slice(0, 2)}.${cnpjLimpo.slice(2, 5)}.${cnpjLimpo.slice(5)}`;
  }
  if (cnpjLimpo.length <= 12) {
    return `${cnpjLimpo.slice(0, 2)}.${cnpjLimpo.slice(2, 5)}.${cnpjLimpo.slice(5, 8)}/${cnpjLimpo.slice(8)}`;
  }
  // Base CNPJ formatted: XX.XXX.XXX/XXXX-XX
  const baseMask = `${cnpjLimpo.slice(0, 2)}.${cnpjLimpo.slice(2, 5)}.${cnpjLimpo.slice(5, 8)}/${cnpjLimpo.slice(8, 12)}-${cnpjLimpo.slice(12, 14)}`;

  // Allow suffix (free text after the 14-digit mask)
  if (cnpjLimpo.length > 14) {
    // Append anything typed beyond the base mask from the original value
    const suffix = value.slice(baseMask.length);
    return baseMask + suffix;
  }
  return baseMask;
}

/**
 * Aplica máscara de CPF durante digitação
 * @param value - Valor atual do input
 * @returns Valor formatado com máscara de CPF
 */
export function mascaraCPF(value: string): string {
  const cpfLimpo = limparDocumento(value);

  if (cpfLimpo.length <= 3) {
    return cpfLimpo;
  }
  if (cpfLimpo.length <= 6) {
    return `${cpfLimpo.slice(0, 3)}.${cpfLimpo.slice(3)}`;
  }
  if (cpfLimpo.length <= 9) {
    return `${cpfLimpo.slice(0, 3)}.${cpfLimpo.slice(3, 6)}.${cpfLimpo.slice(6)}`;
  }
  // Base CPF formatted: XXX.XXX.XXX-XX
  const baseMask = `${cpfLimpo.slice(0, 3)}.${cpfLimpo.slice(3, 6)}.${cpfLimpo.slice(6, 9)}-${cpfLimpo.slice(9, 11)}`;

  // Allow suffix (free text after the 11-digit mask)
  if (cpfLimpo.length > 11) {
    const suffix = value.slice(baseMask.length);
    return baseMask + suffix;
  }
  return baseMask;
}

/**
 * Aplica máscara de telefone brasileiro durante digitação
 * @param value - Valor atual do input
 * @returns Valor formatado com máscara de telefone (11) 98765-4321 ou (11) 2345-6789
 */
export function mascaraTelefone(value: string): string {
  const telefoneLimpo = value.replace(/\D/g, '');

  if (telefoneLimpo.length <= 2) {
    return telefoneLimpo.length > 0 ? `(${telefoneLimpo}` : '';
  }
  if (telefoneLimpo.length <= 6) {
    return `(${telefoneLimpo.slice(0, 2)}) ${telefoneLimpo.slice(2)}`;
  }
  if (telefoneLimpo.length <= 10) {
    // Telefone fixo: (11) 2345-6789
    return `(${telefoneLimpo.slice(0, 2)}) ${telefoneLimpo.slice(2, 6)}-${telefoneLimpo.slice(6)}`;
  }
  // Celular: (11) 98765-4321
  return `(${telefoneLimpo.slice(0, 2)}) ${telefoneLimpo.slice(2, 7)}-${telefoneLimpo.slice(7, 11)}`;
}

/**
 * Valida um telefone brasileiro
 * @param telefone - String contendo o telefone com ou sem formatação
 * @returns true se o telefone é válido (10 ou 11 dígitos), false caso contrário
 */
export function validarTelefone(telefone: string): boolean {
  const telefoneLimpo = telefone.replace(/\D/g, '');
  // Telefone fixo: 10 dígitos, Celular: 11 dígitos
  return telefoneLimpo.length === 10 || telefoneLimpo.length === 11;
}
